import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EnumerationType } from "../../../modules/enumerations/enums";
import { vehicleColorApi } from "../../../modules/vehicle/color/api";
import { CreateUpdateCertificateVehicleColor } from "../../../modules/vehicle/color/types";
import { EntityObject, SearchPageRequest } from "../../types";
import { invalidateEnumeration } from "./enumerations";

export const VEHICLE_COLOR_QUERY_KEYS = {
  filterVehicleColors: (payload?: SearchPageRequest) =>
    payload ? ["filterVehicleColors", payload] : ["filterVehicleColors"],
  createVehicleColor: () => ["createVehicleColor"],
  updateVehicleColor: () => ["updateVehicleColor"],
  deleteVehicleColor: () => ["deleteVehicleColor"],
};

export const useFilterVehicleColors = (payload: SearchPageRequest) => {
  return useQuery({
    queryKey: VEHICLE_COLOR_QUERY_KEYS.filterVehicleColors(payload),
    queryFn: async () => {
      const { data } = await vehicleColorApi.filterVehicleColors(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateVehicleColor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_COLOR_QUERY_KEYS.createVehicleColor(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateCertificateVehicleColor) => {
      const { data } = await vehicleColorApi.createVehicleColor(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.VEHICLE_COLORS);
      queryClient.invalidateQueries({ queryKey: VEHICLE_COLOR_QUERY_KEYS.filterVehicleColors() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateVehicleColor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_COLOR_QUERY_KEYS.updateVehicleColor(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateCertificateVehicleColor>) => {
      const { data } = await vehicleColorApi.updateVehicleColor(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.VEHICLE_COLORS);
      queryClient.invalidateQueries({ queryKey: VEHICLE_COLOR_QUERY_KEYS.filterVehicleColors() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteVehicleColor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: VEHICLE_COLOR_QUERY_KEYS.deleteVehicleColor(),
    mutationFn: (id: string) => {
      return vehicleColorApi.deleteVehicleColor({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.VEHICLE_COLORS);
      queryClient.invalidateQueries({ queryKey: VEHICLE_COLOR_QUERY_KEYS.filterVehicleColors() });
      Notification.itemDeletedNotification();
    },
  });
};
